import { faqData } from "@/data/faqData";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HelpCircle, ArrowRight } from "lucide-react";

export const SEOContent = () => {
    // Get top FAQs for homepage (mix of different categories)
    const homepageFAQs = [
        faqData.find(f => f.id === "what-is-indiekeys"),
        faqData.find(f => f.id === "developer-setup"),
        faqData.find(f => f.id === "request-keys"),
        faqData.find(f => f.id === "key-usage"),
        faqData.find(f => f.id === "player-coins-system"),
        faqData.find(f => f.id === "key-timing"),
    ].filter(Boolean);

    return (
        <section className="bg-muted/30 py-16">
            <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12">
                        {/* Colonne 1 */}
                        <div>
                            <h2 className="text-2xl font-bold mb-4">What is IndieKeys?</h2>
                            <p className="text-muted-foreground mb-4">
                                IndieKeys is the leading platform connecting indie game developers with content creators and players worldwide. Our mission is to help independent game studios gain visibility and reach their target audience through strategic key distribution and press kit management.
                            </p>
                            <p className="text-muted-foreground mb-4">
                                Whether you're a game developer looking to promote your latest indie title, a content creator searching for new games to review, or a player eager to discover hidden gems, IndieKeys provides the tools and community you need to succeed.
                            </p>

                            <h3 className="text-xl font-semibold mt-6 mb-3">For Indie Game Developers</h3>
                            <p className="text-muted-foreground mb-4">
                                Manage your game keys efficiently, create professional press kits, and connect with verified content creators. Track your key distribution, monitor coverage, and grow your studio's reach with our comprehensive analytics dashboard.
                            </p>
                        </div>

                        {/* Colonne 2 */}
                        <div>
                            <h3 className="text-xl font-semibold mb-3">For Content Creators</h3>
                            <p className="text-muted-foreground mb-4">
                                Discover new indie games before they launch, request free game keys for review, and build relationships with indie developers. Our platform makes it easy to find games that match your content style and audience preferences.
                            </p>

                            <h3 className="text-xl font-semibold mt-6 mb-3">For Players</h3>
                            <p className="text-muted-foreground mb-4">
                                Complete missions, earn coins, and unlock exclusive game keys. Join a community of indie game enthusiasts and get early access to upcoming titles. Support independent developers while discovering your next favorite game.
                            </p>

                            <h3 className="text-xl font-semibold mt-6 mb-3">Why Choose IndieKeys?</h3>
                            <ul className="text-muted-foreground space-y-2">
                                <li>✓ Free platform for developers, creators, and players</li>
                                <li>✓ Verified content creator network</li>
                                <li>✓ Advanced press kit builder</li>
                                <li>✓ Real-time analytics and tracking</li>
                                <li>✓ Secure key distribution system</li>
                                <li>✓ Active community support</li>
                            </ul>
                        </div>
                    </div>

                    {/* Section FAQ avec style amélioré */}
                    <div className="mt-12 pt-12 border-t">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-3 flex items-center justify-center gap-3">
                                <HelpCircle className="w-8 h-8 text-primary" />
                                Frequently Asked Questions
                            </h2>
                            <p className="text-muted-foreground">
                                Quick answers to common questions about IndieKeys
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {homepageFAQs.map((faq) => (
                                <Card key={faq?.id} className="overflow-hidden rounded-custom-paddings hover:bg-muted/50 transition-colors">
                                    <div className="p-6">
                                        <div className="flex items-start gap-3 mb-3">
                                            <Badge variant="secondary" className="text-xs mt-1">
                                                {faq?.category}
                                            </Badge>
                                        </div>
                                        <h3 className="font-semibold text-lg mb-3">{faq?.question}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">
                                            {faq?.answer}
                                        </p>
                                    </div>
                                </Card>
                            ))}
                        </div>

                        <div className="text-center mt-8">
                            <Button asChild size="lg" className="gap-2">
                                <a href="/help">
                                    View All FAQs
                                    <ArrowRight className="w-4 h-4" />
                                </a>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
